/**
 * Elementos da ação "Semana da Fisioterapia" para aplicar em LPs de produto
 * sem mexer na identidade da página (mesmo padrão das LPs da Star e da
 * Certificação Lombar no Wix): faixa/banner com logo, desconto e contador.
 *
 * A faixa NÃO é fixa (não trava no topo) — é um banner que rola com a página.
 * O contador zera todos os dias às 23h59 (horário de Brasília).
 */
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import semanaLogo from "@/assets/semanadafisio/logo-horizontal.png.asset.json";
import semanaBg from "@/assets/semanadafisio/banner-bg.png.asset.json";
import { msUntilNextResetBRT } from "@/lib/campanhas/reset-semana";

const GREEN = "#4CD964";

function useResetCountdown() {
  const [ms, setMs] = useState(0);
  useEffect(() => {
    setMs(msUntilNextResetBRT());
    const id = setInterval(() => setMs(msUntilNextResetBRT()), 1000);
    return () => clearInterval(id);
  }, []);
  const total = Math.max(0, Math.floor(ms / 1000));
  return [
    [String(Math.floor(total / 86400)).padStart(2, "0"), "Dias"],
    [String(Math.floor((total % 86400) / 3600)).padStart(2, "0"), "Horas"],
    [String(Math.floor((total % 3600) / 60)).padStart(2, "0"), "Minutos"],
    [String(total % 60).padStart(2, "0"), "Segundos"],
  ] as Array<[string, string]>;
}

function Countdown() {
  const cells = useResetCountdown();
  return (
    <div
      className="inline-flex items-center justify-center rounded-full bg-black/45 px-5 py-3 sm:px-7"
      aria-live="off"
    >
      {cells.map(([v, label], i) => (
        <div key={label} className="flex items-center">
          <div className="flex w-[3.5rem] flex-col items-center sm:w-[4rem]">
            <span
              className="font-mono text-2xl font-extrabold tabular-nums leading-none sm:text-3xl"
              style={{ color: GREEN }}
            >
              {v}
            </span>
            <span className="mt-1 text-[0.55rem] font-semibold uppercase tracking-widest leading-none text-white/60">
              {label}
            </span>
          </div>
          {i < cells.length - 1 && (
            <span className="mx-1 h-8 w-px bg-white/15" aria-hidden />
          )}
        </div>
      ))}
    </div>
  );
}

const bgStyle = {
  backgroundImage: `url(${semanaBg.url})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
} as const;

/** Conteúdo comum: logo empilhada + desconto + tagline + contador (tudo centralizado). */
function FaixaConteudo({ off, label }: { off: string; label?: string }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-8 text-center sm:py-12">
      <img
        src={semanaLogo.url}
        alt="Semana da Fisioterapia"
        className="h-14 w-auto sm:h-16"
        loading="eager"
      />
      <span
        className="mt-3 text-sm font-extrabold uppercase tracking-wide sm:text-base"
        style={{ color: GREEN }}
      >
        {label ?? (off === "80%" ? "AGENTE DE IA COM 80% DE DESCONTO" : `CERTIFICAÇÕES COM ${off} DE DESCONTO`)}
      </span>
      <p
        className="mt-4 text-[#FFBC57] font-extrabold text-lg sm:text-xl md:text-2xl uppercase tracking-wider sm:mt-5"
      >
        ÚLTIMAS HORAS
      </p>
      <div className="mt-4">
        <Countdown />
      </div>
    </div>
  );
}

/** Banner no topo da LP (rola com a página, não fica travado). */
export function SemanaFisioTopBar({
  off,
  href = "/semanadafisio",
  label,
}: { off: string; href?: string; label?: string }) {
  return (
    <a
      href={href}
      className="block w-full bg-[#04353A] text-white transition hover:brightness-110"
      style={bgStyle}
      aria-label={`Semana da Fisioterapia, ${off} de desconto`}
    >
      <FaixaConteudo off={off} label={label} />
    </a>
  );
}

/** Mesma faixa da abertura, para usar junto ao bloco de preço/checkout. */
export function SemanaFisioOfferStrip({ off, label, className }: { off: string; label?: string; className?: string }) {
  return (
    <div className={cn("overflow-hidden rounded-2xl bg-[#04353A] text-white", className)} style={bgStyle}>
      <FaixaConteudo off={off} label={label} />
    </div>
  );
}
